/**
 * The cable.
 *
 * Everything the Add-devices flow needs from a device plugged into this laptop,
 * as an interface: write the firmware, hand over the kit's network, ask who it
 * is. The real one speaks Web Serial through ESP Web Tools and Improv; the one
 * in the unit tests is a fake that answers instantly. The flow itself
 * (`flow.ts`) cannot tell them apart, which is what makes it testable without
 * a board on the desk.
 */

/** What a device says about itself over USB, per K3 and §4 of the kit spec. */
export interface DeviceIdentity {
  /** `cue-` and ten lowercase base32 characters of sha256(publicKey). */
  deviceId: string;
  /** Ed25519 public key, base64url. */
  publicKey: string;
  /** The firmware version it is running, semver. */
  fw: string;
  /**
   * What the device's own board did, when the firmware says.
   *
   * `"dark"` means it is running and talking but its screen never came up —
   * which a person holding a black rectangle cannot otherwise find out without
   * a serial cable they do not have. Absent on firmware from before this
   * existed, which is not the same as `"ok"` and is not reported as one.
   */
  board?: string;
}

export interface DeviceLink {
  /** K2 step 1: write the current firmware. Progress is 0–100 while writing. */
  flash(onProgress: (percent: number) => void): Promise<void>;
  /** K2 step 2: hand over the kit's network name and password. */
  sendNetwork(ssid: string, psk: string): Promise<void>;
  /** K2 step 3: ask the device who it is. */
  readIdentity(): Promise<DeviceIdentity>;
  /** Lets go of the port. Safe to call more than once. */
  close(): Promise<void>;
}

export type ProvisioningStep = "flash" | "network" | "identity" | "enrol";

/** In the order they happen, which is also the order they are shown. */
export const PROVISIONING_STEPS: readonly ProvisioningStep[] = [
  "flash",
  "network",
  "identity",
  "enrol",
];

/**
 * A failure a person can read.
 *
 * `key` is an i18n key — the sentence under the failed step — never prose from
 * a library. The original error rides along as `cause` for the console.
 */
export class ProvisioningError extends Error {
  readonly step: ProvisioningStep;
  readonly key: string;
  readonly params: Record<string, unknown>;

  constructor(
    step: ProvisioningStep,
    key: string,
    options: { cause?: unknown; params?: Record<string, unknown> } = {}
  ) {
    super(key, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = "ProvisioningError";
    this.step = step;
    this.key = key;
    this.params = options.params ?? {};
  }
}
